/**
 * CardForm — native credit card input form with real-time validation.
 *
 * Uses card-validation utils for Luhn check, brand detection, formatting.
 * All fields use design tokens; no hardcoded hex/font/spacing values.
 */
import { useState, useCallback, useRef, type ReactNode } from 'react'
import { View, Text, TextInput as RNTextInput, Platform } from 'react-native'
import { useTranslation } from 'react-i18next'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import { CreditCard } from '@/components/ui/icons'

import { TextInput } from '@/components/ui/TextInput'
import { Button } from '@/components/ui/Button'
import { CheckoutPayFooter } from '@/components/payments/CheckoutPayFooter'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'
import {
  luhnCheck,
  detectCardBrand,
  formatCardNumber,
  formatExpiry,
  parseExpiry,
  isExpired,
  cvvLength,
} from '@/lib/utils/card-validation'
import type { CardPaymentData } from '@/lib/types/payment.types'
import { formatCurrency } from '@/lib/utils/format'

interface CardFormProps {
  /** Integer cents — money never crosses component boundaries as float dollars. */
  amountCents: number
  onSubmit: (data: CardPaymentData) => void
  loading?: boolean
  disabled?: boolean
  submitTitle?: string
  footer?: ReactNode
  trustText?: string
  /**
   * The name the account already carries, used as the cardholder's default.
   *
   * A card's embossed name is usually its owner's, and the person filling this
   * form told us theirs at signup. Offering it saves a line of typing on a
   * screen where every extra keystroke sits between somebody and a payment.
   * Editable, always: a card can belong to a company or a spouse.
   */
  defaultCardholderName?: string
}

interface FieldErrors {
  card?: string
  expiry?: string
  cvv?: string
  name?: string
}

const BRAND_LABELS: Record<string, string> = {
  visa: 'VISA',
  mastercard: 'MC',
  amex: 'AMEX',
}

// Digit masks, not copy: they are the same in every locale, exactly like the
// "MM/AA" already on the expiry field. Without them the card number was a wide
// empty slab with a lone card glyph at its right edge and the CVV an empty box,
// sitting next to a sibling field that clearly invited input — the group read
// as half disabled. The grouping matches what `formatCardNumber` produces.
const CARD_NUMBER_PLACEHOLDER = '1234 5678 9012 3456'
const CVV_PLACEHOLDER_3 = '123'
const CVV_PLACEHOLDER_4 = '1234'

export function CardForm({
  amountCents,
  onSubmit,
  loading = false,
  disabled = false,
  submitTitle,
  footer,
  trustText,
  defaultCardholderName,
}: CardFormProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const [cardRaw, setCardRaw] = useState('')
  const [expiryRaw, setExpiryRaw] = useState('')
  const [cvv, setCvv] = useState('')
  const [name, setName] = useState(defaultCardholderName ?? '')
  const [errors, setErrors] = useState<FieldErrors>({})

  const expiryRef = useRef<RNTextInput>(null)
  const cvvRef = useRef<RNTextInput>(null)
  const nameRef = useRef<RNTextInput>(null)

  const brand = detectCardBrand(cardRaw)
  const maxCvv = cvvLength(brand)

  const handleCardChange = useCallback(
    (text: string) => {
      const digits = text.replace(/\D/g, '').slice(0, 16)
      setCardRaw(digits)
      if (errors.card) setErrors((prev) => ({ ...prev, card: undefined }))
    },
    [errors.card],
  )

  const handleExpiryChange = useCallback(
    (text: string) => {
      const digits = text.replace(/\D/g, '').slice(0, 4)
      setExpiryRaw(digits)
      if (errors.expiry) setErrors((prev) => ({ ...prev, expiry: undefined }))
    },
    [errors.expiry],
  )

  const handleCvvChange = useCallback(
    (text: string) => {
      const digits = text.replace(/\D/g, '').slice(0, maxCvv)
      setCvv(digits)
      if (errors.cvv) setErrors((prev) => ({ ...prev, cvv: undefined }))
    },
    [errors.cvv, maxCvv],
  )

  const handleNameChange = useCallback(
    (text: string) => {
      setName(text)
      if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }))
    },
    [errors.name],
  )

  const validate = useCallback((): FieldErrors => {
    const errs: FieldErrors = {}

    // Card number
    if (!cardRaw) {
      errs.card = t('card_form.card_required')
    } else if (!luhnCheck(cardRaw)) {
      errs.card = t('card_form.invalid_card')
    }

    // Expiry
    if (!expiryRaw) {
      errs.expiry = t('card_form.expiry_required')
    } else {
      const formatted = formatExpiry(expiryRaw)
      const parsed = parseExpiry(formatted)
      if (!parsed) {
        errs.expiry = t('card_form.invalid_expiry')
      } else if (isExpired(parsed.month, parsed.year)) {
        errs.expiry = t('card_form.card_expired')
      }
    }

    // CVV
    if (!cvv) {
      errs.cvv = t('card_form.cvv_required')
    } else if (cvv.length !== maxCvv) {
      errs.cvv = t('card_form.invalid_cvv')
    }

    return errs
  }, [cardRaw, expiryRaw, cvv, maxCvv, t])

  const handleSubmit = useCallback(() => {
    // `disabled` no era una propiedad del formulario: era una propiedad del
    // BOTON, y solo del boton (linea ~304). El teclado tiene su propia puerta
    // —`onSubmitEditing` en el campo del nombre— y esa puerta llamaba aca
    // directo, sin preguntarle a nadie. O sea que alguien con el checkout
    // bloqueado por verificacion apretaba "Listo" en el teclado y disparaba el
    // pago igual, para que el servidor lo rechazara con un mensaje que no
    // explica nada. La guarda tiene que vivir en la accion, no en el control
    // que la dibuja: los controles se multiplican, la accion es una.
    if (disabled) {
      if (Platform.OS !== 'web') {
        void notification(NotificationType.Error)
      }
      return
    }

    const errs = validate()
    setErrors(errs)

    if (Object.keys(errs).length > 0) {
      if (Platform.OS !== 'web') {
        void notification(NotificationType.Error)
      }
      return
    }

    if (Platform.OS !== 'web') {
      impact(ImpactStyle.Light)
    }

    const formatted = formatExpiry(expiryRaw)
    const parsed = parseExpiry(formatted)!

    onSubmit({
      cardNumber: cardRaw,
      cvv,
      expiryMonth: parsed.month,
      expiryYear: parsed.year,
      cardholderName: name || undefined,
    })
  }, [disabled, validate, cardRaw, cvv, expiryRaw, name, onSubmit])

  const isFormFilled = cardRaw.length >= 13 && expiryRaw.length === 4 && cvv.length === maxCvv

  // Brand indicator for rightIcon
  const brandIcon =
    brand !== 'unknown' ? (
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 12,
          color: colors.textSecondary,
          letterSpacing: 0.5,
        }}
      >
        {BRAND_LABELS[brand]}
      </Text>
    ) : (
      <CreditCard size={20} color={colors.textSecondary} />
    )

  return (
    <View>
      {/* Card number */}
      <TextInput
        label={t('card_form.card_number')}
        value={formatCardNumber(cardRaw)}
        onChangeText={handleCardChange}
        placeholder={CARD_NUMBER_PLACEHOLDER}
        keyboardType="number-pad"
        inputMode="numeric"
        autoComplete="cc-number"
        textContentType="creditCardNumber"
        error={errors.card}
        rightIcon={brandIcon}
        returnKeyType="next"
        onSubmitEditing={() => expiryRef.current?.focus()}
        testID="card-form.number"
        accessibilityLabel={t('card_form.card_number')}
      />

      {/* Expiry + CVV row.
          `minWidth: 0` is load-bearing: a flex item defaults to min-width auto,
          so it refuses to shrink below its content and pushes out of the row
          instead. On a 390px phone the CVV field ran 28px off the right edge of
          the payment form. */}
      <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <TextInput
            ref={expiryRef}
            label={t('card_form.expiry')}
            value={formatExpiry(expiryRaw)}
            onChangeText={handleExpiryChange}
            placeholder="MM/AA"
            keyboardType="number-pad"
            inputMode="numeric"
            autoComplete="cc-exp"
            error={errors.expiry}
            returnKeyType="next"
            onSubmitEditing={() => cvvRef.current?.focus()}
            testID="card-form.expiry"
            accessibilityLabel={t('card_form.expiry')}
          />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <TextInput
            ref={cvvRef}
            label={t('card_form.cvv')}
            value={cvv}
            onChangeText={handleCvvChange}
            placeholder={maxCvv === 4 ? CVV_PLACEHOLDER_4 : CVV_PLACEHOLDER_3}
            keyboardType="number-pad"
            inputMode="numeric"
            autoComplete="cc-csc"
            maxLength={maxCvv}
            secureTextEntry
            error={errors.cvv}
            returnKeyType="next"
            onSubmitEditing={() => nameRef.current?.focus()}
            testID="card-form.cvv"
            accessibilityLabel={t('card_form.cvv')}
          />
        </View>
      </View>

      {/* Cardholder name (optional).
          The last field of the group and the only one still blank once the
          card number and CVV got their masks — a wide empty slab under three
          filled siblings reads as disabled. A name has no format to
          demonstrate, so this one carries an example rather than a mask, using
          the same "Ej:" convention the rest of the app's example placeholders
          use. Full-width field, so the full two-word name fits. */}
      <TextInput
        ref={nameRef}
        label={t('card_form.cardholder_name')}
        placeholder={t('card_form.cardholder_name_placeholder')}
        value={name}
        onChangeText={handleNameChange}
        autoCapitalize="words"
        autoComplete="cc-name"
        textContentType="name"
        returnKeyType="done"
        onSubmitEditing={handleSubmit}
        testID="card-form.name"
        accessibilityLabel={t('card_form.cardholder_name')}
      />

      {trustText ? (
        <CheckoutPayFooter
          title={submitTitle ?? t('card_form.pay_amount', { amount: formatCurrency(amountCents) })}
          onPress={handleSubmit}
          loading={loading}
          disabled={disabled || !isFormFilled}
          testID="card-form.submit"
          trustText={trustText}
        />
      ) : (
        <>
          <View style={{ marginTop: SPACING.sm }}>
            <Button
              title={
                submitTitle ?? t('card_form.pay_amount', { amount: formatCurrency(amountCents) })
              }
              variant="primary"
              size="lg"
              loading={loading}
              disabled={disabled || !isFormFilled}
              onPress={handleSubmit}
              testID="card-form.submit"
            />
          </View>
          {footer ? <View style={{ marginTop: SPACING.sm }}>{footer}</View> : null}
        </>
      )}
    </View>
  )
}
